import {InputSystemProvider, toast, useRegisterAction} from '@wandb/components';
import React, {type PropsWithChildren, useCallback} from 'react';

import {INPUT_ACTION, inputSystem} from '../inputSystem';
import {
  keyboardShortcutFailed,
  keyboardShortcutUsed,
} from '../services/analytics';
import {useColorMode} from '../util/useColorMode';
import {KeyboardShortcutsDrawer} from './KeyboardShortcutsDrawer/KeyboardShortcutsDrawer';

export const InputSystem: React.FC<PropsWithChildren> = ({children}) => {
  const onError = useCallback<
    NonNullable<React.ComponentProps<typeof InputSystemProvider>['onError']>
  >(({binding, error}) => {
    keyboardShortcutFailed({
      currentScope: binding.scope,
      shortcutId: binding.id,
      shortcutName: binding.name,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    toast.error(`${binding.name} failed.`);
  }, []);

  const onAction = useCallback<
    NonNullable<React.ComponentProps<typeof InputSystemProvider>['onAction']>
  >(({binding, currentScope}) => {
    keyboardShortcutUsed({
      currentScope,
      shortcutId: binding.id,
      shortcutName: binding.name,
    });
  }, []);

  return (
    <InputSystemProvider
      system={inputSystem}
      onError={onError}
      onAction={onAction}>
      <InputSystemRegistry>{children}</InputSystemRegistry>
      <KeyboardShortcutsDrawer />
    </InputSystemProvider>
  );
};

const InputSystemRegistry = ({children}: PropsWithChildren) => {
  useInputSystemRegistry();
  return <>{children}</>;
};

// Register global, self-contained actions here.
const useInputSystemRegistry = () => {
  const {isDarkMode, updateColorMode} = useColorMode();
  useRegisterAction(INPUT_ACTION.TOGGLE_APP_THEME, () => {
    updateColorMode(isDarkMode ? 'light' : 'dark');
  });
};
